import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { propertyService } from '../../service/property.service';
import { Property } from '../../models/property.model';
import {
  createPaginatedSliceState,
  CACHE_DURATION,
  isCacheValid,
  PaginatedState,
} from '../utils/apiSliceUtils';

// State interface
interface PropertiesState {
  list: PaginatedState<Property>;
  searchResults: {
    data: Property[];
    loading: boolean;
    error: string | null;
    lastFetch: number | null;
    searchParams: {
      cityId?: number;
      checkIn?: string;
      checkOut?: string;
      roomTypeId?: number;
    } | null;
  };
  byCity: {
    [cityId: number]: {
      data: Property[];
      loading: boolean;
      error: string | null;
      lastFetch: number | null;
      cacheTimeout: number;
    };
  };
  byCityAndRmc: {
    [key: string]: { // key format: "cityId_rmcId"
      data: Property[];
      loading: boolean;
      error: string | null;
      lastFetch: number | null;
      cacheTimeout: number;
    };
  };
  currentProperty: {
    data: Property | null;
    loading: boolean;
    error: string | null;
  };
}

// Initial state
const initialState: PropertiesState = {
  list: createPaginatedSliceState<Property>(10),
  searchResults: {
    data: [],
    loading: false,
    error: null,
    lastFetch: null,
    searchParams: null,
  },
  byCity: {},
  byCityAndRmc: {},
  currentProperty: {
    data: null,
    loading: false,
    error: null,
  },
};

// Async thunks
export const fetchPropertiesPaginated = createAsyncThunk<
  { data: Property[]; totalRecords: number },
  { pageNumber?: number; pageSize?: number; filters?: Record<string, any> },
  { rejectValue: string }
>(
  'properties/fetchPropertiesPaginated',
  async ({ pageNumber = 1, pageSize = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await propertyService.getAllPaginated({
        pageNumber,
        pageSize,
        ...filters,
      });
      
      if (!response) {
        return { data: [], totalRecords: 0 };
      }
      
      return {
        data: Array.isArray(response.items) ? response.items : (response.items as any)?.$values || [],
        totalRecords: response.totalCount || 0,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch properties');
    }
  }
);

export const fetchPropertiesByCity = createAsyncThunk<
  Property[],
  number,
  { rejectValue: string }
>(
  'properties/fetchPropertiesByCity',
  async (cityId, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { properties: PropertiesState };
      const cityState = state.properties.byCity[cityId];
      
      // Check cache validity
      if (cityState?.data && isCacheValid(cityState.lastFetch, cityState.cacheTimeout)) {
        return cityState.data;
      }
      
      const data = await propertyService.getPropertiesByCityId(cityId);
      return Array.isArray(data) ? data : (data as any)?.$values || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch properties by city');
    }
  }
);

export const fetchPropertiesByCityAndRmc = createAsyncThunk<
  Property[],
  { cityId: number; rmcId: number },
  { rejectValue: string }
>(
  'properties/fetchPropertiesByCityAndRmc',
  async ({ cityId, rmcId }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { properties: PropertiesState };
      const key = `${cityId}_${rmcId}`;
      const cacheState = state.properties.byCityAndRmc[key];
      
      // Check cache validity
      if (cacheState?.data && isCacheValid(cacheState.lastFetch, cacheState.cacheTimeout)) {
        return cacheState.data;
      }
      
      const data = await propertyService.getPropertiesByCityIdAndRmcId(cityId, rmcId);
      return Array.isArray(data) ? data : (data as any)?.$values || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch properties by city and RMC');
    }
  }
);

export const searchProperties = createAsyncThunk<
  Property[],
  { cityId: number; checkIn: string; checkOut: string; roomTypeId: number },
  { rejectValue: string }
>(
  'properties/searchProperties',
  async (searchParams, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { properties: PropertiesState };
      const currentSearch = state.properties.searchResults;
      
      // Check if same search parameters and cache is valid
      if (
        currentSearch.searchParams &&
        JSON.stringify(currentSearch.searchParams) === JSON.stringify(searchParams) &&
        currentSearch.lastFetch &&
        isCacheValid(currentSearch.lastFetch, CACHE_DURATION.SHORT)
      ) {
        return currentSearch.data;
      }
      
      const data = await propertyService.searchProperties(
        searchParams.cityId,
        searchParams.checkIn,
        searchParams.checkOut,
        searchParams.roomTypeId
      );
      return Array.isArray(data) ? data : (data as any)?.$values || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to search properties');
    }
  }
);

export const fetchPropertyById = createAsyncThunk<
  Property,
  number,
  { rejectValue: string }
>(
  'properties/fetchPropertyById',
  async (propertyId, { rejectWithValue }) => {
    try {
      const data = await propertyService.getById(propertyId);
      if (!data) {
        return rejectWithValue('Property not found');
      }
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch property');
    }
  }
);

export const createProperty = createAsyncThunk<
  Property,
  Property,
  { rejectValue: string }
>(
  'properties/createProperty',
  async (propertyData, { rejectWithValue }) => {
    try {
      const data = await propertyService.createProperty(propertyData);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create property');
    }
  }
);

export const updateProperty = createAsyncThunk<
  Property,
  { id: number; data: Property },
  { rejectValue: string }
>(
  'properties/updateProperty',
  async ({ id, data }, { rejectWithValue }) => {
    try {
  // use multipart/form-data aware update for images and other collections
  const updatedData = await propertyService.updateProperty(id, data);
      return updatedData;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update property');
    }
  }
);

export const deleteProperty = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>(
  'properties/deleteProperty',
  async (propertyId, { rejectWithValue }) => {
    try {
      await propertyService.delete(propertyId);
      return propertyId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete property');
    }
  }
);

// Properties slice
const propertiesSlice = createSlice({
  name: 'properties',
  initialState,
  reducers: {
    // Clear actions
    clearSearchResults: (state) => {
      state.searchResults.data = [];
      state.searchResults.lastFetch = null;
      state.searchResults.searchParams = null;
      state.searchResults.error = null;
    },
    clearCurrentProperty: (state) => {
      state.currentProperty.data = null;
      state.currentProperty.error = null;
    },
    clearPropertiesCache: (state) => {
      state.byCity = {};
      state.byCityAndRmc = {};
      state.searchResults.data = [];
      state.searchResults.lastFetch = null;
      state.searchResults.searchParams = null;
    },
    // Filter and pagination
    setPropertiesFilters: (state, action) => {
      state.list.filters = action.payload;
      state.list.currentPage = 1; // Reset to first page when filters change
    },
    setPropertiesPage: (state, action) => {
      state.list.currentPage = action.payload;
    },
    setPropertiesPageSize: (state, action) => {
      state.list.pageSize = action.payload;
      state.list.currentPage = 1; // Reset to first page when page size changes
    },
    // Invalidate caches
    invalidatePropertiesCache: (state) => {
      state.list.lastFetch = null;
      Object.keys(state.byCity).forEach(cityId => {
        state.byCity[parseInt(cityId)].lastFetch = null;
      });
      Object.keys(state.byCityAndRmc).forEach(key => {
        state.byCityAndRmc[key].lastFetch = null;
      });
      state.searchResults.lastFetch = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch properties paginated
    builder
      .addCase(fetchPropertiesPaginated.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchPropertiesPaginated.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload.data;
        state.list.totalRecords = action.payload.totalRecords;
        state.list.lastFetch = Date.now();
        state.list.error = null;
      })
      .addCase(fetchPropertiesPaginated.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload || 'Failed to fetch properties';
      });

    // Fetch properties by city
    builder
      .addCase(fetchPropertiesByCity.pending, (state, action) => {
        const cityId = action.meta.arg;
        if (!state.byCity[cityId]) {
          state.byCity[cityId] = {
            data: [],
            loading: false,
            error: null,
            lastFetch: null,
            cacheTimeout: CACHE_DURATION.SHORT,
          };
        }
        state.byCity[cityId].loading = true;
        state.byCity[cityId].error = null;
      })
      .addCase(fetchPropertiesByCity.fulfilled, (state, action) => {
        const cityId = action.meta.arg;
        state.byCity[cityId].loading = false;
        state.byCity[cityId].data = action.payload;
        state.byCity[cityId].lastFetch = Date.now();
        state.byCity[cityId].error = null;
      })
      .addCase(fetchPropertiesByCity.rejected, (state, action) => {
        const cityId = action.meta.arg;
        state.byCity[cityId].loading = false;
        state.byCity[cityId].error = action.payload || 'Failed to fetch properties by city';
      });

    // Fetch properties by city and RMC
    builder
      .addCase(fetchPropertiesByCityAndRmc.pending, (state, action) => {
        const { cityId, rmcId } = action.meta.arg;
        const key = `${cityId}_${rmcId}`;
        if (!state.byCityAndRmc[key]) {
          state.byCityAndRmc[key] = {
            data: [],
            loading: false,
            error: null,
            lastFetch: null,
            cacheTimeout: CACHE_DURATION.SHORT,
          };
        }
        state.byCityAndRmc[key].loading = true;
        state.byCityAndRmc[key].error = null;
      })
      .addCase(fetchPropertiesByCityAndRmc.fulfilled, (state, action) => {
        const { cityId, rmcId } = action.meta.arg;
        const key = `${cityId}_${rmcId}`;
        state.byCityAndRmc[key].loading = false;
        state.byCityAndRmc[key].data = action.payload;
        state.byCityAndRmc[key].lastFetch = Date.now();
        state.byCityAndRmc[key].error = null;
      })
      .addCase(fetchPropertiesByCityAndRmc.rejected, (state, action) => {
        const { cityId, rmcId } = action.meta.arg;
        const key = `${cityId}_${rmcId}`;
        state.byCityAndRmc[key].loading = false;
        state.byCityAndRmc[key].error = action.payload || 'Failed to fetch properties by city and RMC';
      });

    // Search properties
    builder
      .addCase(searchProperties.pending, (state) => {
        state.searchResults.loading = true;
        state.searchResults.error = null;
      })
      .addCase(searchProperties.fulfilled, (state, action) => {
        state.searchResults.loading = false;
        state.searchResults.data = action.payload;
        state.searchResults.lastFetch = Date.now();
        state.searchResults.searchParams = action.meta.arg;
        state.searchResults.error = null;
      })
      .addCase(searchProperties.rejected, (state, action) => {
        state.searchResults.loading = false;
        state.searchResults.error = action.payload || 'Failed to search properties';
      });

    // Fetch property by ID
    builder
      .addCase(fetchPropertyById.pending, (state) => {
        state.currentProperty.loading = true;
        state.currentProperty.error = null;
      })
      .addCase(fetchPropertyById.fulfilled, (state, action) => {
        state.currentProperty.loading = false;
        state.currentProperty.data = action.payload;
        state.currentProperty.error = null;
      })
      .addCase(fetchPropertyById.rejected, (state, action) => {
        state.currentProperty.loading = false;
        state.currentProperty.error = action.payload || 'Failed to fetch property';
      });

    // Create property
    builder
      .addCase(createProperty.pending, (state) => {
        state.currentProperty.loading = true;
        state.currentProperty.error = null;
      })
      .addCase(createProperty.fulfilled, (state, action) => {
        state.currentProperty.loading = false;
        state.currentProperty.data = action.payload;
        // Add to list if loaded
        if (state.list.data.length > 0) {
          state.list.data.unshift(action.payload);
          state.list.totalRecords += 1;
        }
        // Invalidate caches
        Object.keys(state.byCity).forEach(cityId => {
          state.byCity[parseInt(cityId)].lastFetch = null;
        });
        Object.keys(state.byCityAndRmc).forEach(key => {
          state.byCityAndRmc[key].lastFetch = null;
        });
        state.currentProperty.error = null;
      })
      .addCase(createProperty.rejected, (state, action) => {
        state.currentProperty.loading = false;
        state.currentProperty.error = action.payload || 'Failed to create property';
      });

    // Update property
    builder
      .addCase(updateProperty.pending, (state) => {
        state.currentProperty.loading = true;
        state.currentProperty.error = null;
      })
      .addCase(updateProperty.fulfilled, (state, action) => {
        state.currentProperty.loading = false;
        state.currentProperty.data = action.payload;
        
        // Update in list if present
        const index = state.list.data.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.list.data[index] = action.payload;
        }
        
        // Update in city cache if present
        const cityId = action.payload.cityId;
        if (state.byCity[cityId]) {
          const cityIndex = state.byCity[cityId].data.findIndex(p => p.id === action.payload.id);
          if (cityIndex !== -1) {
            state.byCity[cityId].data[cityIndex] = action.payload;
          }
        }
        
        // Invalidate related caches
        Object.keys(state.byCityAndRmc).forEach(key => {
          state.byCityAndRmc[key].lastFetch = null;
        });
        
        state.currentProperty.error = null;
      })
      .addCase(updateProperty.rejected, (state, action) => {
        state.currentProperty.loading = false;
        state.currentProperty.error = action.payload || 'Failed to update property';
      });

    // Delete property
    builder
      .addCase(deleteProperty.pending, (state) => {
        state.currentProperty.loading = true;
        state.currentProperty.error = null;
      })
      .addCase(deleteProperty.fulfilled, (state, action) => {
        state.currentProperty.loading = false;
        const deletedId = action.payload;
        
        // Remove from list
        state.list.data = state.list.data.filter(p => p.id !== deletedId);
        state.list.totalRecords -= 1;
        
        // Remove from city caches
        Object.keys(state.byCity).forEach(cityId => {
          state.byCity[parseInt(cityId)].data = state.byCity[parseInt(cityId)].data.filter(p => p.id !== deletedId);
        });
        
        // Remove from city and RMC caches
        Object.keys(state.byCityAndRmc).forEach(key => {
          state.byCityAndRmc[key].data = state.byCityAndRmc[key].data.filter(p => p.id !== deletedId);
        });
        
        // Remove from search results
        state.searchResults.data = state.searchResults.data.filter(p => p.id !== deletedId);
        
        // Clear current property if it was deleted
        if (state.currentProperty.data?.id === deletedId) {
          state.currentProperty.data = null;
        }
        
        state.currentProperty.error = null;
      })
      .addCase(deleteProperty.rejected, (state, action) => {
        state.currentProperty.loading = false;
        state.currentProperty.error = action.payload || 'Failed to delete property';
      });
  },
});

export const {
  clearSearchResults,
  clearCurrentProperty,
  clearPropertiesCache,
  setPropertiesFilters,
  setPropertiesPage,
  setPropertiesPageSize,
  invalidatePropertiesCache,
} = propertiesSlice.actions;

export default propertiesSlice.reducer;
